/**
 * Where sign-up lands (§5.1): an authenticated user with no membership. One
 * field, because the tenant home this creates answers the questions a wizard
 * would ask -- there are no plans to choose between (§19), seats are free,
 * and the volume question belongs to the estimator, which needs a campaign
 * to exist first.
 *
 * Drawn as step 1 of 2 (Figma `148:1532`). Step 1 sends nothing: it holds the
 * name, and step 2, `Capacity.tsx` (`354:13640`), renders in its place and is
 * where the organisation is created, as both frames draw it.
 *
 * The card nests its own brand mark rather than using `AuthFrame` (unlike
 * sign-in/sign-up/reset-password/accept-invitation): the frame's card shows
 * the mark inside the card, not above it, and `AuthFrame` is shared by those
 * other screens, which do not draw it that way.
 */
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { Activity } from 'lucide-react';

import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import { Field, FieldDescription, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { tt } from '../i18n';
import { Capacity } from './Capacity';
import { PageRoot } from './PageRoot';

export function Onboarding(): ReactNode {
  const nameId = useId();
  const hintId = useId();
  const [name, setName] = useState('');
  const [organisationName, setOrganisationName] = useState<string | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    setOrganisationName(trimmed);
  }

  if (organisationName !== null) {
    return <Capacity organisationName={organisationName} />;
  }

  return (
    <PageRoot>
      <Card className="w-full">
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <span className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Activity aria-hidden className="size-4" />
            </span>
            {/* eslint-disable-next-line no-restricted-syntax -- the product's name, not copy: it does not change with locale */}
            <span className="text-base font-semibold text-foreground">SocialSrc</span>
          </div>

          <div className="flex items-center gap-2">
            <span aria-hidden className="flex items-center gap-1.5">
              <span className="h-1 w-6 rounded-full bg-primary" />
              <span className="h-1 w-2 rounded-full bg-border" />
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              {tt('onboarding.stepLabel')}
            </span>
          </div>

          <h1 className="text-2xl font-semibold text-foreground">{tt('onboarding.title')}</h1>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Field>
              <FieldLabel htmlFor={nameId}>{tt('onboarding.name.label')}</FieldLabel>
              <Input
                id={nameId}
                name="name"
                value={name}
                aria-describedby={hintId}
                onChange={(event) => setName(event.target.value)}
              />
              <FieldDescription id={hintId}>{tt('onboarding.name.hint')}</FieldDescription>
            </Field>

            <div className="flex justify-end">
              <Button type="submit">{tt('onboarding.submit')}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </PageRoot>
  );
}
